import React, { Component } from 'react';

// Catalog must stay in sync with additionalServices.js (details section)
// so wizard selections flow directly into the correct preset toggles.
const ADDITIONAL_SERVICES = [
	{
		label: 'Airport Transfers',
		icon: 'las la-shuttle-van',
		description: 'Ground transportation and airport pick-up/drop-off coordination for attendees.'
	},
	{
		label: 'Audio Visual (A/V)',
		icon: 'las la-video',
		description: 'Professional sound, lighting, screens, and technical direction for sessions.'
	},
	{
		label: 'Décor & Design',
		icon: 'las la-palette',
		description: 'Creative décor, theming, floral, and environmental design services.'
	},
	{
		label: 'Entertainment',
		icon: 'las la-music',
		description: 'Live music, performers, DJs, and entertainment programming.'
	},
	{
		label: 'Event Photography',
		icon: 'las la-camera',
		description: 'Professional photo and video capture throughout the event.'
	},
	{
		label: 'Floral Services',
		icon: 'las la-seedling',
		description: 'Floral arrangements, centerpieces, and botanical design.'
	},
	{
		label: 'Guest Speakers',
		icon: 'las la-microphone',
		description: 'Keynote speakers, facilitators, and expert presenters.'
	},
	{
		label: 'Off-site Activities',
		icon: 'las la-hiking',
		description: 'Excursions, team outings, and off-property experiences.'
	},
	{
		label: 'Production Services',
		icon: 'las la-film',
		description: 'Full-scale event production including staging, rigging, and show management.'
	},
	{
		label: 'Security Services',
		icon: 'las la-user-shield',
		description: 'On-site security personnel and access control.'
	},
	{
		label: 'Shuttle Services',
		icon: 'las la-bus',
		description: 'Group shuttle transportation between hotels, venues, and activity sites.'
	},
	{
		label: 'Signage & Branding',
		icon: 'las la-sign',
		description: 'Custom signage, banners, branded materials, and wayfinding.'
	},
	{
		label: 'Spa Services',
		icon: 'las la-spa',
		description: 'On-site spa treatments, wellness activities, or spa buyouts.'
	},
	{
		label: 'Team Building',
		icon: 'las la-puzzle-piece',
		description: 'Facilitated team-building activities and group engagement programs.'
	},
	{
		label: 'VIP Services',
		icon: 'las la-star',
		description: 'Elevated concierge, private experiences, and VIP guest management.'
	},
	{
		label: 'WiFi/Internet Upgrades',
		icon: 'las la-wifi',
		description: 'Dedicated bandwidth, hardline internet, or upgraded connectivity for the event.'
	}
];

const PRESET_SERVICE_LABELS = ADDITIONAL_SERVICES.map((s) => s.label);

class AdditionalServicesStep extends Component {
	constructor(props) {
		super(props);
		this.state = {
			customServiceInput: ''
		};
	}

	toggleService = (label) => {
		const { data, onChange } = this.props;
		const selectedServices = [...(data.additionalServices || [])];
		const idx = selectedServices.indexOf(label);
		if (idx >= 0) {
			selectedServices.splice(idx, 1);
		} else {
			selectedServices.push(label);
		}
		onChange('additionalServices', selectedServices);
	};

	addCustomService = () => {
		const { data, onChange } = this.props;
		const value = this.state.customServiceInput.trim();
		if (!value) return;

		const selectedServices = [...(data.additionalServices || [])];
		if (selectedServices.includes(value)) return;

		onChange('additionalServices', [...selectedServices, value]);
		this.setState({ customServiceInput: '' });
	};

	removeCustomService = (service) => {
		const { data, onChange } = this.props;
		const selectedServices = (data.additionalServices || []).filter((s) => s !== service);
		onChange('additionalServices', selectedServices);
	};

	render() {
		const { data } = this.props;
		const { customServiceInput } = this.state;
		const selectedServices = data.additionalServices || [];
		const customServices = selectedServices.filter((s) => !PRESET_SERVICE_LABELS.includes(s));

		return (
			<div className="rfp-step-content">
				<h1 className="rfp-step-title">Additional Services</h1>

				<p className="rfp-additional-services-desc">
					Select any onsite services you would like additional information on.
				</p>

				<div className="rfp-services-grid">
					{ADDITIONAL_SERVICES.map((service) => {
						const isSelected = selectedServices.includes(service.label);
						return (
							<div
								key={service.label}
								className={`rfp-service-card ${isSelected ? 'selected' : ''}`}
								onClick={() => this.toggleService(service.label)}
							>
								<div className="rfp-service-image-placeholder">
									<i className={`${service.icon} rfp-service-icon`} />
								</div>
								<div className="rfp-service-card-body">
									<div className="rfp-service-title">{service.label}</div>
									<div className="rfp-service-desc">{service.description}</div>
								</div>
							</div>
						);
					})}
				</div>

				<div className="rfp-field-group" style={{ marginTop: 28 }}>
					<label className="rfp-field-label">Add a service not listed above</label>
					<div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
						<div style={{ flex: 1 }}>
							<input
								className="rfp-text-input"
								type="text"
								value={customServiceInput}
								onChange={(e) => this.setState({ customServiceInput: e.target.value })}
								onKeyDown={(e) => {
									if (e.key === 'Enter') {
										e.preventDefault();
										this.addCustomService();
									}
								}}
								placeholder="Type a service and press Enter"
							/>
							<div className="rfp-input-underline" />
						</div>
						<button
							type="button"
							className="rfp-add-concession-btn"
							onClick={this.addCustomService}
							disabled={!customServiceInput.trim()}
							style={{
								display: 'inline-flex',
								alignItems: 'center',
								gap: 6,
								padding: '8px 16px',
								background: customServiceInput.trim() ? '#1a1a1a' : '#d5d5d5',
								color: '#fff',
								border: 'none',
								borderRadius: 20,
								fontSize: 13,
								fontWeight: 600,
								cursor: customServiceInput.trim() ? 'pointer' : 'not-allowed',
								whiteSpace: 'nowrap',
								flexShrink: 0
							}}
						>
							<i className="las la-plus" />
							Add
						</button>
					</div>
				</div>

				{customServices.length > 0 && (
					<div className="rfp-concession-tags">
						{customServices.map((service) => (
							<div key={service} className="rfp-concession-tag rfp-concession-tag-custom">
								{service}
								<button
									className="rfp-tag-remove"
									onClick={() => this.removeCustomService(service)}
								>
									<i className="las la-times" />
								</button>
							</div>
						))}
					</div>
				)}
			</div>
		);
	}
}

export default AdditionalServicesStep;
